import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

const FEATURES = ["billing_feature_1", "billing_feature_2", "billing_feature_3", "billing_feature_4", "billing_feature_5"];

function loadPaypalScript(clientId, currency) {
  return new Promise((resolve, reject) => {
    if (window.paypal && window.paypal.HostedButtons) return resolve(window.paypal);
    const existing = document.getElementById("paypal-sdk-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "paypal-sdk-script";
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&components=hosted-buttons&disable-funding=venmo&currency=${currency}`;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const monthlyRef = useRef(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans || []);
      setPaypalCfg(r.data.paypal);
    }).catch(() => {});
  }, []);

  // Render the embedded monthly PayPal hosted button.
  useEffect(() => {
    if (!paypalCfg?.client_id || !paypalCfg?.hosted_button_id_monthly) return;
    if (!monthlyRef.current) return;
    let cancelled = false;
    loadPaypalScript(paypalCfg.client_id, paypalCfg.currency || "EUR")
      .then((paypal) => {
        if (cancelled || !monthlyRef.current) return;
        monthlyRef.current.innerHTML = "";
        try {
          paypal.HostedButtons({ hostedButtonId: paypalCfg.hosted_button_id_monthly })
            .render(monthlyRef.current);
        } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [paypalCfg]);

  const labelKey = i18n.language === "es" ? "label_es" : "label_en";

  function payAnnual() {
    if (!user) {
      navigate("/register");
      return;
    }
    const url = paypalCfg?.payment_link_annual;
    if (!url) return;
    window.open(url, "_blank", "noopener");
    navigate("/billing?awaiting=1");
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block bg-[#FFC53D] border-2 border-[#1C1917] px-3 py-1 text-[10px] font-black tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917]">
            {t("billing_trial_badge")}
          </span>
          <h1 className="mt-6 hero-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1C1917]">
            {t("billing_pricing_title")}
          </h1>
          <p className="mt-4 text-base text-[#57534E]">{t("billing_pricing_sub")}</p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-8">
          {plans.map((plan) => {
            const isAnnual = plan.id === "annual";
            return (
              <div
                key={plan.id}
                className={`relative border-2 border-[#1C1917] p-8 shadow-[6px_6px_0_0_#1C1917] transition-transform hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#1C1917] flex flex-col ${
                  isAnnual ? "bg-[#FFE5A0]" : "bg-white"
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isAnnual && (
                  <span
                    className="absolute -top-4 right-6 bg-[#1C1917] text-[#FDFBF7] px-3 py-1 text-[10px] font-black tracking-widest uppercase border-2 border-[#1C1917]"
                    data-testid="badge-save"
                  >
                    {t("billing_save_badge")}
                  </span>
                )}
                <span className="eyebrow text-xs">
                  {isAnnual ? t("billing_plan_annual") : t("billing_plan_monthly")}
                </span>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="display text-5xl font-black text-[#1C1917]">
                    {plan[labelKey].split(" ")[0]}
                  </span>
                  <span className="text-sm text-[#57534E] font-bold">
                    {plan[labelKey].split(" ").slice(1).join(" ")}
                  </span>
                </div>

                <p className="mt-4 text-sm font-bold text-[#1C1917]">
                  {t("billing_features_title")}
                </p>
                <ul className="mt-3 space-y-2 flex-1">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className="mt-0.5 inline-grid place-items-center w-5 h-5 bg-[#1C1917] text-[#FDFBF7] font-black text-[11px] border-2 border-[#1C1917]">✓</span>
                      <span className="text-[#1C1917]">{t(f)}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA per plan */}
                <div className="mt-6 pt-6 border-t-2 border-[#1C1917]">
                  {isAnnual ? (
                    <Button
                      onClick={payAnnual}
                      disabled={!paypalCfg?.payment_link_annual}
                      className="w-full rounded-none h-12 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black tracking-widest uppercase text-sm shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                      data-testid="btn-pay-annual"
                    >
                      {t("paypal_pay_annual")} →
                    </Button>
                  ) : (
                    <div ref={monthlyRef} className="min-h-[60px]" data-testid="paypal-monthly-container" />
                  )}
                  {!isAnnual && !paypalCfg?.client_id && (
                    <p className="text-xs text-[#E5484D]">PayPal aún no configurado.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Activation instructions */}
        <div className="mt-12 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] p-8 shadow-[6px_6px_0_0_#FFC53D]" data-testid="paypal-after-card">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-2">
              <span className="eyebrow text-xs text-[#FFC53D]">{t("paypal_after_title")}</span>
              <h2 className="display text-2xl mt-2">{t("paypal_after_subtitle")}</h2>
              <ol className="mt-4 space-y-2 text-sm leading-relaxed list-decimal list-inside text-[#FDFBF7]">
                <li>{t("paypal_after_step1")}</li>
                <li>{t("paypal_after_step2")}</li>
                <li>{t("paypal_after_step3")}</li>
              </ol>
            </div>
            {user && (
              <div className="flex items-center md:justify-end">
                <Button
                  onClick={() => navigate("/billing")}
                  className="rounded-none h-12 px-6 border-2 border-[#FDFBF7] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-black tracking-widest uppercase text-sm shadow-[4px_4px_0_0_#FFC53D] hover:shadow-[6px_6px_0_0_#FFC53D] hover:-translate-y-[1px] transition-all"
                  data-testid="btn-go-billing"
                >
                  {t("paypal_go_to_billing")} →
                </Button>
              </div>
            )}
          </div>
        </div>
        {!user && (
          <div className="mt-8 text-center">
            <Link
              to="/register"
              className="inline-flex items-center h-12 px-6 text-sm tracking-widest uppercase font-black text-[#1C1917] underline underline-offset-4 decoration-2 hover:text-[#7C3AED]"
            >
              {t("cta_start")} →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
